import React from "react";
import { Box, Button, Card, Stack, Text, Alert } from "braid-design-system";
import Loading from "../components/Loading";
import { useAuth } from "../contexts/AuthProvider";
import { useToast } from "../hooks/useToast";
import {
    createCashReceipt,
    getAllCashReceipts,
    CashReceiptResponse,
    CashReceiptCreateRequest,
} from "../services/cashReceiptService";
import { sanitizeNumericString, isValidNumericString } from "../utils/numericInput";

const getCurrentDateTime = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");

    return {
        date: `${yyyy}-${mm}-${dd}`,
        time: `${hh}:${min}`,
    };
};

const toRequestPayload = (payload: {
    payment_mode: string;
    location_id: string;
    cashier_id: string;
    station_id: string;
    payment_date: string;
    payment_time: string;
    amount: number;
    description: string;
}): CashReceiptCreateRequest => ({
    payment_loc: payload.location_id,
    payment_date: payload.payment_date,
    payment_time: payload.payment_date, // Use same date value for payment_time
    payment_cashier_id: payload.cashier_id,
    payment_station: payload.station_id,
    payment_shift: 1,
    payment_description: payload.description,
    payment_amount: payload.amount,
    payment_mode: payload.payment_mode === "Receipt" ? "R" : "W",
});

const CashReceipt: React.FC = () => {
    const { user } = useAuth();
    const toast = useToast();
    const [paymentMode, setPaymentMode] = React.useState("Receipt");
    const [amount, setAmount] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [locationId, setLocationId] = React.useState("001"); // Default location ID
    const [cashReceiptList, setCashReceiptList] = React.useState<CashReceiptResponse[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    const serverDate = getCurrentDateTime().date;
    const serverTime = getCurrentDateTime().time;
    const loginUserId = user?.user_name ?? "-";
    const stationId = "001";
    const paymentShift = 1;

    const fetchData = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const filters = {
                payment_loc: locationId,
                payment_date: serverDate,
                payment_station: parseFloat(stationId),
                payment_shift: paymentShift,
            };
            const data = await getAllCashReceipts(filters);
            setCashReceiptList(data);
        } catch (fetchError: any) {
            const message = fetchError?.message || "Unable to load cash receipt data.";
            setError(message);
            toast.showError(message);
        } finally {
            setLoading(false);
        }
    }, [toast, locationId, serverDate, stationId, paymentShift]);

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    const validateForm = () => {
        if (!amount.trim()) {
            setFormError("Amount is required.");
            return false;
        }

        if (!isValidNumericString(amount.trim())) {
            setFormError("Amount must be a valid number.");
            return false;
        }

        setFormError(null);
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            setError(null);
            const payload = toRequestPayload({
                payment_mode: paymentMode,
                location_id: locationId,
                cashier_id: loginUserId,
                station_id: stationId,
                payment_date: serverDate,
                payment_time: serverTime,
                amount: Number(amount),
                description: description,
            });

            const result = await createCashReceipt(payload);
            toast.showSuccess(`Cash receipt saved successfully with Payment No: ${result.payment_no}`);
            setPaymentMode("Receipt");
            setAmount("");
            setDescription("");
            setLocationId("001");
            await fetchData();
        } catch (submitError: any) {
            const message = submitError?.message || "Failed to save cash receipt.";
            setError(message);
            toast.showError(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box padding="medium">
            <Card>
                <Box padding="medium">
                    <Stack space="large">
                        <Text weight="strong" size="large">
                            Cash Receipt
                        </Text>

                        {error ? (
                            <Alert tone="critical">
                                <Text>
                                    <span>{error}</span>
                                </Text>
                            </Alert>
                        ) : null}

                        <Card>
                            <Box padding="medium">
                                <Stack space="medium">
                                    {/* First Row: Payment Mode and Amount */}
                                    <Box display="flex" flexDirection="row" gap="small">
                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Payment Mode</Text>
                                            <select
                                                value={paymentMode}
                                                onChange={(e) => setPaymentMode(e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #dfe3e8",
                                                    marginTop: "6px",
                                                }}
                                            >
                                                <option value="Receipt">Receipt</option>
                                                <option value="withdrawa">withdrawa</option>
                                            </select>
                                        </Box>

                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Amount</Text>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={amount}
                                                onChange={(e) => {
                                                    setAmount(sanitizeNumericString(e.target.value));
                                                }}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #dfe3e8",
                                                    marginTop: "6px",
                                                }}
                                                placeholder="Enter amount"
                                            />
                                        </Box>
                                    </Box>

                                    {/* Second Row: Location ID */}
                                    <Box display="flex" flexDirection="row" gap="small">
                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Location ID</Text>
                                            <input
                                                type="text"
                                                value={locationId}
                                                readOnly
                                                disabled
                                                style={{
                                                    width: "100%",
                                                    padding: "10px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #dfe3e8",
                                                    marginTop: "6px",
                                                    backgroundColor: "#f5f5f5",
                                                    color: "#666",
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Third Row: Date, Time, User ID, Station ID - Stacked Vertically */}
                                    <Box display="flex" flexDirection="row" gap="small">
                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Date</Text>
                                            <Box background="surface" borderRadius="standard" padding="small" style={{ marginTop: "6px", textAlign: "left" }}>
                                                <Text>{serverDate}</Text>
                                            </Box>
                                        </Box>

                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Time</Text>
                                            <Box background="surface" borderRadius="standard" padding="small" style={{ marginTop: "6px", textAlign: "left" }}>
                                                <Text>{serverTime}</Text>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box display="flex" flexDirection="row" gap="small">
                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">User ID</Text>
                                            <Box background="surface" borderRadius="standard" padding="small" style={{ marginTop: "6px", textAlign: "left" }}>
                                                <Text>{loginUserId}</Text>
                                            </Box>
                                        </Box>

                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Station ID</Text>
                                            <Box background="surface" borderRadius="standard" padding="small" style={{ marginTop: "6px", textAlign: "left" }}>
                                                <Text>{stationId}</Text>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Description Field */}
                                    <Box>
                                        <Text tone="secondary">Description</Text>
                                        <textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            style={{
                                                width: "100%",
                                                padding: "10px",
                                                borderRadius: "6px",
                                                border: "1px solid #dfe3e8",
                                                marginTop: "6px",
                                                minHeight: "80px",
                                                resize: "vertical",
                                            }}
                                            placeholder="Enter description"
                                        />
                                    </Box>

                                    {formError ? (
                                        <Alert tone="critical">
                                            <Text>
                                                <span>{formError}</span>
                                            </Text>
                                        </Alert>
                                    ) : null}

                                    <Button
                                        variant="solid"
                                        tone="brandAccent"
                                        onClick={handleSubmit}
                                        loading={saving}
                                    >
                                        Save Receipt
                                    </Button>
                                </Stack>
                            </Box>
                        </Card>

                        <Card>
                            <Box padding="medium">
                                <Stack space="medium">
                                    <Text weight="strong">Existing Cash Receipts</Text>
                                    {loading ? (
                                        <Loading message="Loading receipts..." />
                                    ) : cashReceiptList.length === 0 ? (
                                        <Text tone="secondary">No records found.</Text>
                                    ) : (
                                        <Box style={{ overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Payment No</th>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Payment Mode</th>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Location ID</th>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Cashier ID</th>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Station</th>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Date</th>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Time</th>
                                                        <th style={{ textAlign: "right", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Amount</th>
                                                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" }}>Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cashReceiptList.map((receipt) => (
                                                        <tr key={receipt.payment_no ?? `${receipt.payment_date}-${receipt.payment_time}` }>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_no ?? "-"}</td>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_mode ?? "-"}</td>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_loc ?? "-"}</td>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_cashier_id ?? "-"}</td>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_station ?? "-"}</td>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_date ?? "-"}</td>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_time ?? "-"}</td>
                                                            <td style={{ padding: "10px", textAlign: "right", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_amount != null ? receipt.payment_amount.toFixed(2) : "-"}</td>
                                                            <td style={{ padding: "10px", borderBottom: "1px solid #f0f4f8" }}>{receipt.payment_description ?? "-"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        </Card>
                    </Stack>
                </Box>
            </Card>
        </Box>
    );
};

export default CashReceipt;

// ============================================
// SQL Schema for Cash Receipt Table
// ============================================
// Table: pos_cash_rec_with
//
// CREATE TABLE `pos_cash_rec_with` (
//   `Payment_No`          INT             NOT NULL AUTO_INCREMENT,
//   `Payment_Loc`         VARCHAR(5)      NULL,
//   `Payment_Date`        DATETIME        NULL,
//   `Payment_Time`        DATETIME        NULL,
//   `Payment_Cashier_id`  VARCHAR(15)     NULL,
//   `Payment_Station`     FLOAT           NULL,
//   `Payment_Shift`       INT             NULL DEFAULT 1,
//   `Payment_Description` VARCHAR(50)     NULL,
//   `Payment_Amount`      FLOAT           NULL,
//   `Payment_Mode`        CHAR(1)         NULL,
//   `created_at`          DATETIME        NULL,
//   `updated_at`          DATETIME        NULL,
//   PRIMARY KEY (`Payment_No`)
//
// Sample INSERT:
// INSERT INTO `pos_cash_rec_with` (Payment_Loc, Payment_Date, Payment_Time, Payment_Cashier_id, Payment_Station, Payment_Shift, Payment_Description, Payment_Amount, Payment_Mode)
// VALUES ('001', '2026-04-17', '2026-04-17', 'cashier001', '001', 1, 'Cash received', 5000.00, 'R');
//
// Sample SELECT:
// SELECT Payment_No, Payment_Loc, Payment_Date, Payment_Time, Payment_Cashier_id, Payment_Station, Payment_Amount, Payment_Mode FROM `pos_cash_rec_with` ORDER BY Payment_No DESC;